import { useQuery } from '@tanstack/react-query'
import { config } from '@/config'
import {
  fetchRegisteredSourceDataSources,
  fetchRegisteredTargetDataSources,
  resolveAdpStaticAssetUrl,
} from '@/features/adp/api/adpDataSources.api'
import type { AdpRegisteredSourceDataSource } from '@/features/adp/api/adpDataSources.types'
import {
  toDataSourceInstanceLabel,
  toDataSourceTypeFormatted,
} from '@/features/adp/components/sheets/ConfigureDataDestinationSheet/helpers'

export type RegisteredSourcePickerItem = {
  id: string
  rooId?: number
  kind: string
  connectionName: string
  image: string
  username: string
  password: string
  databaseName: string
  endpoint: string
  dataSourceInstance: string
  dataSourceType: string
  dataSourceInstanceLabel: string
  dataSourceTypeFormatted: string
}

function toTitle(input: string): string {
  return input
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(token => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ')
}

function mapRegisteredSourceToPickerItem(
  source: AdpRegisteredSourceDataSource,
  adpApiUrl: string
): RegisteredSourcePickerItem {
  const resolvedId =
    source.source_data_source_id ??
    source.target_data_source_id ??
    source.roo_id ??
    source.data_source_name

  return {
    id: String(resolvedId),
    rooId: typeof source.roo_id === 'number' ? source.roo_id : undefined,
    kind: toTitle(source.data_source_instance || source.data_source_type || 'Source'),
    connectionName: source.data_source_name,
    image: resolveAdpStaticAssetUrl(adpApiUrl, source.image_link),
    username: source.username ?? '',
    password: source.password ?? '',
    databaseName: source.database_name ?? '',
    endpoint: source.endpoint ?? '',
    dataSourceInstance: source.data_source_instance ?? '',
    dataSourceType: source.data_source_type ?? '',
    dataSourceInstanceLabel: toDataSourceInstanceLabel(source.data_source_instance),
    dataSourceTypeFormatted: toDataSourceTypeFormatted(source.data_source_type),
  }
}

/** Maps a list row to picker shape (source or target), using the shared ID resolution rules. */
export function mapToRegisteredPickerItem(source: AdpRegisteredSourceDataSource): RegisteredSourcePickerItem {
  return mapRegisteredSourceToPickerItem(source, config.adpApiUrl)
}

export function useRegisteredSourceDataSources(
  allowedInstance: string | null,
  dateInput: string | null,
  targetName: string | null,
  enabled = true
) {
  return useQuery<AdpRegisteredSourceDataSource[], Error, RegisteredSourcePickerItem[]>({
    queryKey: [
      'adp',
      'source-data-sources',
      'registered',
      allowedInstance ?? 'all',
      dateInput ?? 'all_time',
      targetName ?? '',
    ],
    enabled,
    queryFn: () =>
      fetchRegisteredSourceDataSources({
        date_input: dateInput ?? undefined,
        allowed_instances: allowedInstance ?? undefined,
        source_name: targetName?.trim() || undefined,
      }),
    select: data => data.map(item => mapRegisteredSourceToPickerItem(item, config.adpApiUrl)),
    staleTime: 30_000,
  })
}

export function useRegisteredTargetDataSources(
  allowedInstance: string | null,
  dateInput: string | null,
  targetName: string | null,
  enabled = true
) {
  return useQuery<AdpRegisteredSourceDataSource[], Error, RegisteredSourcePickerItem[]>({
    queryKey: [
      'adp',
      'target-data-sources',
      'registered',
      allowedInstance ?? 'all',
      dateInput ?? 'all_time',
      targetName ?? '',
    ],
    enabled,
    queryFn: () =>
      fetchRegisteredTargetDataSources({
        date_input: dateInput ?? undefined,
        allowed_instances: allowedInstance ?? undefined,
        target_name: targetName?.trim() || undefined,
      }),
    select: data => data.map(item => mapRegisteredSourceToPickerItem(item, config.adpApiUrl)),
    staleTime: 30_000,
  })
}